/**
 * The proposal contract — the single source of truth for a request's shape.
 *
 * Both the browser (src/client) and the server (src/server) import this, so the
 * form and the endpoint cannot drift apart: change the shape and whichever side
 * disagrees stops compiling.
 *
 * Keep it free of runtime dependencies and of anything Node-specific. It is
 * bundled into the browser, so `fs` and `process` must never appear here.
 *
 * The server always re-validates. Client-side validation is a convenience for
 * the visitor, never a security boundary.
 */

/** Locales the site is published in. */
export const LOCALES = ['en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * What kind of work is being asked for.
 *
 * The order these are *shown* in differs by language — Spanish leads with Odoo,
 * English with web platforms — but the stored values never translate, so a
 * request can be counted and filtered regardless of which language it came in.
 */
export const PROJECT_KINDS = ['web', 'mobile', 'odoo', 'integrations', 'rescue'] as const;
export type ProjectKind = (typeof PROJECT_KINDS)[number];

/**
 * Budget bands.
 *
 * Deliberately bands rather than a number: a band is answerable by someone who
 * has not costed the project yet, and "guidance" exists so that not knowing is
 * a valid answer instead of a reason to abandon the form.
 */
export const BUDGET_BANDS = ['under-10k', '10k-30k', '30k-75k', 'over-75k', 'guidance'] as const;
export type BudgetBand = (typeof BUDGET_BANDS)[number];

export function isProjectKind(value: unknown): value is ProjectKind {
  return typeof value === 'string' && (PROJECT_KINDS as readonly string[]).includes(value);
}

export function isBudgetBand(value: unknown): value is BudgetBand {
  return typeof value === 'string' && (BUDGET_BANDS as readonly string[]).includes(value);
}

/** What the browser POSTs to /api/proposal. */
export interface ProposalRequest {
  /** Who is asking. Required — a proposal is addressed to a person. */
  name: string;
  /** Their company. Optional: solo founders are real clients too. */
  company?: string;
  /** Where the reply goes. Required. */
  email: string;
  /** What kind of work. Required. */
  kind: ProjectKind;
  /** Roughly what they can spend. Required, and "guidance" is a valid answer. */
  budget: BudgetBand;
  /** Free text, e.g. "before the March trade show". Optional. */
  deadline?: string;
  /** What they want built. Required — this is the part an engineer reads. */
  detail: string;
  /** Language the visitor was reading in, so the acknowledgement matches. */
  locale?: Locale;
  /**
   * Anti-spam honeypot. A real browser leaves this empty because the field is
   * hidden from humans; most naive bots fill in every input they find.
   *
   * Named `website` rather than `company`, which is the honeypot on the product
   * sites — here `company` is a field a real person fills in, and using it as
   * the trap would silently discard every request from someone who has one.
   */
  website?: string;
}

/** What the API sends back. `ok` discriminates the union. */
export type ProposalResponse =
  | { ok: true; message: string }
  | { ok: false; error: ProposalErrorCode; message: string; field?: ProposalField };

/** Which input to point the visitor at when one specific field is wrong. */
export type ProposalField = 'name' | 'email' | 'kind' | 'budget' | 'detail';

/**
 * Stable, machine-readable failure codes. The browser maps these to translated
 * copy, so the human-readable `message` is only a fallback for non-browser callers.
 */
export type ProposalErrorCode =
  | 'invalid_email'
  | 'missing_email'
  | 'email_too_long'
  | 'missing_name'
  | 'missing_detail'
  | 'detail_too_long'
  | 'field_too_long'
  | 'invalid_kind'
  | 'invalid_budget'
  | 'rate_limited'
  | 'spam_detected'
  | 'send_failed'
  | 'server_error';

/**
 * Practical maximum length of an email address.
 * RFC 5321 caps the whole path at 254 characters.
 */
export const EMAIL_MAX_LENGTH = 254;

/** Caps on the free-text fields. Generous for a human, bounded for a mailbox. */
export const NAME_MAX_LENGTH = 120;
export const COMPANY_MAX_LENGTH = 160;
export const DEADLINE_MAX_LENGTH = 120;
export const DETAIL_MIN_LENGTH = 10;
export const DETAIL_MAX_LENGTH = 4000;

/**
 * Deliberately permissive email pattern.
 *
 * Fully validating an address per RFC 5322 is famously impossible with a regex,
 * and being strict here rejects real people (long TLDs, plus-addressing,
 * apostrophes). The only proof an address works is a delivered email, so this
 * check is only meant to catch obvious typos before we spend a send on them.
 */
const EMAIL_PATTERN = /^[^\s@,;:<>()[\]\\]+@[^\s@.,;:<>()[\]\\]+(\.[^\s@.,;:<>()[\]\\]+)+$/;

export interface EmailValidationResult {
  valid: boolean;
  /** Trimmed and lower-cased — safe to store and compare. */
  normalized: string;
  error?: ProposalErrorCode;
}

/** Trim, lower-case and sanity-check an email address. */
export function validateEmail(input: unknown): EmailValidationResult {
  if (typeof input !== 'string') {
    return { valid: false, normalized: '', error: 'missing_email' };
  }

  const normalized = input.trim().toLowerCase();

  if (normalized.length === 0) {
    return { valid: false, normalized, error: 'missing_email' };
  }
  if (normalized.length > EMAIL_MAX_LENGTH) {
    return { valid: false, normalized, error: 'email_too_long' };
  }
  if (!EMAIL_PATTERN.test(normalized)) {
    return { valid: false, normalized, error: 'invalid_email' };
  }

  return { valid: true, normalized };
}

/** A request that passed validation, with every field normalised. */
export interface ValidProposal {
  name: string;
  company: string;
  email: string;
  kind: ProjectKind;
  budget: BudgetBand;
  deadline: string;
  detail: string;
  locale: Locale;
}

export interface ProposalValidationResult {
  valid: boolean;
  value?: ValidProposal;
  error?: ProposalErrorCode;
  field?: ProposalField;
}

/**
 * Validate and normalise a whole request.
 *
 * Shared rather than living in the route, because the browser runs exactly this
 * to decide whether to enable the submit button. One implementation means the
 * form can never accept something the server will reject, or vice versa.
 */
export function validateProposal(input: unknown): ProposalValidationResult {
  if (typeof input !== 'object' || input === null) {
    return { valid: false, error: 'server_error' };
  }

  const body = input as Record<string, unknown>;
  const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

  const name = text(body.name);
  if (name.length === 0) return { valid: false, error: 'missing_name', field: 'name' };
  if (name.length > NAME_MAX_LENGTH) {
    return { valid: false, error: 'field_too_long', field: 'name' };
  }

  const company = text(body.company);
  if (company.length > COMPANY_MAX_LENGTH) {
    return { valid: false, error: 'field_too_long', field: 'name' };
  }

  const email = validateEmail(body.email);
  if (!email.valid) return { valid: false, error: email.error, field: 'email' };

  if (!isProjectKind(body.kind)) return { valid: false, error: 'invalid_kind', field: 'kind' };
  if (!isBudgetBand(body.budget)) return { valid: false, error: 'invalid_budget', field: 'budget' };

  const deadline = text(body.deadline);
  if (deadline.length > DEADLINE_MAX_LENGTH) {
    return { valid: false, error: 'field_too_long', field: 'detail' };
  }

  const detail = text(body.detail);
  if (detail.length < DETAIL_MIN_LENGTH) {
    return { valid: false, error: 'missing_detail', field: 'detail' };
  }
  if (detail.length > DETAIL_MAX_LENGTH) {
    return { valid: false, error: 'detail_too_long', field: 'detail' };
  }

  return {
    valid: true,
    value: {
      name,
      company,
      email: email.normalized,
      kind: body.kind,
      budget: body.budget,
      deadline,
      detail,
      locale: isLocale(body.locale) ? body.locale : DEFAULT_LOCALE,
    },
  };
}
